/* eslint-disable react/no-unknown-property */
import React, { useMemo } from "react";
import type { ButtonProps } from "@tinper/next-ui";
import { Row, Col, Button, Space } from "@tinper/next-ui";
import DataForm from "../../DataForm/src";
import omit from 'lodash/omit';
import i18n from "./i18n";

/** @name 用于配置操作栏 */
export type SearchConfig = {
  /** @name 重置按钮的文本 */
  resetText?: React.ReactNode;
  /** @name 提交按钮的文本 */
  submitText?: React.ReactNode;
};
export type SubmitterProps<T = Record<string, any>> = {
  /** @name fieldid */
  fieldid?: string;
  /** @name 提交方法 */
  onSubmit?: (value?: T) => void;
  /** @name 重置方法 */
  onReset?: (value?: T) => void;
  /** @name 搜索的配置，一般用来配置文本 */
  searchConfig?: SearchConfig;
  /** @name 提交按钮的 props */
  submitButtonProps?: false | (ButtonProps & { preventDefault?: boolean });
  /** @name 重置按钮的 props */
  resetButtonProps?: false | (ButtonProps & { preventDefault?: boolean });
  /** @name 自定义操作的渲染 */
  render?:
    | ((
        props: SubmitterProps &
          T & {
            submit: () => void;
            reset: () => void;
          },
        dom: any
      ) => any)
    | false;
};

/**
 * FormFooter 的组件，可以自动进行一些配置
 *
 * @param props
 */

const Submitter: any = (props: { render: any; onSubmit?: any; onSearch?: any; onReset?: any; searchConfig?: any; submitButtonProps?: any; resetButtonProps?: any; spaceProps?: any; fieldid?: any; locale: string }) => {
  const formRef = DataForm.useFormInstance();
  if (props.render === false) {
    return null;
  }

  const {
    onSubmit,
    onSearch,
    render,
    onReset,
    searchConfig = {},
    submitButtonProps,
    resetButtonProps = {},
    spaceProps = {},
    fieldid,
    locale
  } = props;

  const submit = () => {
    let pValues: any, pErrors: any;
    return new Promise((resolve, reject) => {
      // form.formRef.current.submit();
      formRef
        .validateFields()
        .then((values: unknown) => {
          pValues = values;
          resolve(values);
        })
        .catch((err: any) => {
          pErrors = err;
          reject(err);
        })
        .finally(() => {
          onSearch?.(pValues, pErrors, formRef);
          onSubmit?.(pValues, pErrors, formRef);
        });
    });
  };

  const reset = () => {
    const prevValues = formRef.getFieldsValue();
    formRef.resetFields();
    onReset?.(prevValues);
  };

  function langTransform (lang: string) {
    if (!lang) return lang
    const reg = /([a-zA-Z]{2})[_-]?([A-Za-z]+)/g
    return lang.replace(reg, '$1-$2').toLowerCase()
  }

  const langMap = useMemo(() => {
    // @ts-ignore
    return i18n[langTransform(locale) || 'zh-cn'] || {};
  }, [locale])

  const { resetText = langMap.reset } = searchConfig;
  /** 默认的操作的逻辑 */
  const dom: any = [];

  if (submitButtonProps !== false) {
    dom.push(
      <Button
        style={{
          width: "42px"
        }}
        colors="dark"
        icon="uf-search"
        fieldid={fieldid ? `${fieldid}_submit` : undefined}
        {...omit(submitButtonProps || {}, ["preventDefault"])}
        onClick={(e: any) => {
          if (!submitButtonProps?.preventDefault) submit();
          submitButtonProps?.onClick?.(
            e as any
          );
        }}
      />
    );
    if (resetButtonProps !== false) {
      dom.push(
        <Button
          type="text"
          size="lg"
          style={{
            minWidth: "auto",
            padding: 0
          }}
          fieldid={fieldid ? `${fieldid}_reset` : undefined}
          {...omit(resetButtonProps, ["preventDefault"])}
          key="rest"
          onClick={(e: any) => {
            if (!resetButtonProps?.preventDefault) reset();
            resetButtonProps?.onClick?.(
              e as any
            );
          }}
        >
          {resetText}
        </Button>
      );
    }
  }

  const renderDom = render
    ? render({ ...props, form: formRef.current, submit, reset }, dom)
    : dom;
  if (!renderDom) {
    return null;
  }
  if (Array.isArray(renderDom)) {
    if (renderDom?.length < 1) {
      return null;
    }
    if (renderDom?.length === 1) {
      return renderDom[0] as any;
    }
    return (
      <div
        className="as-searchForm-submitter"
        style={{
          marginLeft: "8px"
        }}
        fieldid={fieldid ? `${fieldid}_submitter` : undefined}
      >
        <Row justify="end">
          <Col>
            <Space {...spaceProps}>{renderDom}</Space>
          </Col>
        </Row>
      </div>
    );
  }
  return renderDom as any;
};

export default Submitter;
